import React from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useFormAutosaveLoad } from 'hooks/useFormAutosave';
import { useGetProductsByIds } from 'queries/products/products.fetchById';
import { useDeleteAutosave } from 'queries/autosave/autosave';
import {
  addSelectedProduct,
  clearSelectedProducts,
  selectSelectedIds,
} from 'store/products';
import { clearAdded, clearCampaignInfo } from 'store/campaigns';
import { isAutosaveStale } from 'queries/autosave/autosave.helpers';

const STALE_DRAFT_MAX_AGE_DAYS = 3;

interface UseCampaignAutosaveHydrationOptions {
  logScope?: string;
}

interface UseCampaignAutosaveHydrationResult {
  autosaveData: any;
  isLoading: boolean;
  selectedProductIds: number[];
}

/**
 * Restores the autosaved draft's products into Redux on mount, or wipes them
 * if the draft is stale. Staleness is checked before idsToHydrate is set —
 * otherwise the async product fetch could resolve after a stale-clear and
 * re-add the old products. Unlike ClearDraftButton, a stale draft is wiped
 * entirely, including products.
 */
export const useCampaignAutosaveHydration = ({
  logScope = 'CampaignAutosaveHydration',
}: UseCampaignAutosaveHydrationOptions = {}): UseCampaignAutosaveHydrationResult => {
  const dispatch = useAppDispatch();
  const selectedProductIds = useAppSelector(selectSelectedIds);
  const isAdded = useAppSelector(state => state.campaigns.isAdded);

  const { loadedData: autosaveData, isLoading } = useFormAutosaveLoad(
    'campaign/create',
    true,
  );
  const deleteAutosaveMutation = useDeleteAutosave();

  const selectedProductIdsEmpty = React.useRef(selectedProductIds.length === 0);
  const hydrationInitializedRef = React.useRef(false);
  const hydratedProductIdsRef = React.useRef<Set<number | string>>(new Set());
  const [idsToHydrate, setIdsToHydrate] = React.useState<(number | string)[]>(
    [],
  );

  React.useEffect(() => {
    if (isLoading || hydrationInitializedRef.current) return;
    hydrationInitializedRef.current = true;

    const isStale = Boolean(
      autosaveData?.timestamp &&
        isAutosaveStale(autosaveData.timestamp, STALE_DRAFT_MAX_AGE_DAYS),
    );

    if (isStale) {
      console.log(`[${logScope}] Autosave draft is stale -> clearing`);
      dispatch(clearSelectedProducts());
      dispatch(clearCampaignInfo());
      dispatch(clearAdded());
      deleteAutosaveMutation.mutate('campaign/create');
      setIdsToHydrate([]);
      return;
    }

    if (isAdded) {
      setIdsToHydrate([]);
      return;
    }

    if (!selectedProductIdsEmpty.current) {
      console.log(`[${logScope}] Redux not empty on start -> skip hydration`);
      setIdsToHydrate([]);
      return;
    }

    const ids = Array.isArray(autosaveData?.products)
      ? autosaveData.products
      : [];
    console.log(`[${logScope}] idsToHydrate (frozen):`, ids);
    setIdsToHydrate(ids);
  }, [
    isLoading,
    autosaveData,
    isAdded,
    dispatch,
    deleteAutosaveMutation,
    logScope,
  ]);

  const productQueries = useGetProductsByIds(idsToHydrate);
  console.log(`[${logScope}] productQueries:`, productQueries);

  React.useEffect(() => {
    if (idsToHydrate.length === 0) return;

    productQueries.forEach((result: typeof productQueries[number]) => {
      if (result.isSuccess && result.data) {
        const id = result.data.id;
        const alreadyHydrated = hydratedProductIdsRef.current.has(id);

        if (!alreadyHydrated) {
          console.log(`[${logScope}] hydrate addSelectedProduct:`, id);
          dispatch(addSelectedProduct(result.data));
          hydratedProductIdsRef.current.add(id);
        }
      }
    });
  }, [productQueries, dispatch, idsToHydrate.length, logScope]);

  return {
    autosaveData,
    isLoading,
    selectedProductIds,
  };
};
